import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import { useLocation, useParams } from "react-router";

import { useProject } from "../../contexts/project";
import { useProjectInventory } from "../../contexts/projectInventory";
import { WAREHOUSE_NAMES, FETCH_CLIENT_QUERY } from "../../constants/graphql/queries";

const normalizeWarehouse = ({ warehouseId, name, id }) => ({
  warehouseId: warehouseId || id,
  id,
  name,
});

const normalizeClientId = clientId => {
  const normalizedClientId = String(clientId || "").trim();
  if (!normalizedClientId) return undefined;

  const lowerCasedClientId = normalizedClientId.toLowerCase();
  if (["null", "undefined", "all"].includes(lowerCasedClientId)) {
    return undefined;
  }

  return normalizedClientId;
};

export default function useAvailableWarehouses() {
  const { clientId: routeClientId } = useParams();
  const { pathname } = useLocation();
  const { clientId: selectedClientId, inputProjectId } = useProjectInventory();
  const currentProject = useProject();

  const pathnameClientId = useMemo(() => {
    const match = pathname?.match(/^\/clients\/([^/]+)/);
    return match?.[1];
  }, [pathname]);

  const normalizedRouteClientId = normalizeClientId(routeClientId);
  const normalizedPathnameClientId = normalizeClientId(pathnameClientId);
  const normalizedSelectedClientId = normalizeClientId(selectedClientId);
  const clientScopeId =
    normalizedRouteClientId ||
    normalizedPathnameClientId ||
    (inputProjectId === "all" ? normalizedSelectedClientId : undefined);

  const { data: { warehouses: allWarehouses = [] } = {} } = useQuery(WAREHOUSE_NAMES, {
    fetchPolicy: "cache-first",
    variables: { userWarehouses: true },
    skip: currentProject?.warehouses?.length > 0 || !!clientScopeId,
  });

  const { data: clientData } = useQuery(FETCH_CLIENT_QUERY, {
    fetchPolicy: "cache-first",
    variables: { id: clientScopeId },
    skip: !clientScopeId,
  });

  const warehouses = useMemo(() => {
    if (clientScopeId) {
      const clientProjects = clientData?.client?.projects || [];
      const allowedWarehouseIds = new Set(
        (clientData?.client?.clientsWarehouses || [])
          .map(entry => entry?.warehouse?.id)
          .filter(Boolean)
          .map(String)
      );

      const allClientProjectWarehouses = clientProjects
        .flatMap(project => project.warehouses || [])
        .filter(warehouse => {
          if (!allowedWarehouseIds.size) return true;
          return warehouse?.id && allowedWarehouseIds.has(String(warehouse.id));
        })
        .map(normalizeWarehouse)
        .filter(warehouse => warehouse.warehouseId);

      return sortBy(uniqBy(allClientProjectWarehouses, "warehouseId"), "name");
    }

    if (currentProject?.warehouses?.length > 0) {
      return sortBy(currentProject.warehouses.map(normalizeWarehouse), "name");
    }

    return sortBy(allWarehouses.map(normalizeWarehouse), "name");
  }, [
    clientScopeId,
    clientData?.client?.projects,
    clientData?.client?.clientsWarehouses,
    currentProject?.warehouses,
    allWarehouses,
  ]);

  return warehouses;
}
